/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {Injectable, inject} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import {Store} from '@ngrx/store';
import {map, Observable, of, skipWhile, switchMap, take} from 'rxjs';
import {TokenSelectors} from '@shared/common-core/store/selectors';
import {AuthSelectors} from '@shared/modules/auth/store/selectors';

/**
 * The guard is used to check if the user is logged in. The Angular CanActivate guard decides, if a route can be activated (or component gets rendered).
 */
@Injectable({
  providedIn: 'root',
})
export class LoggedOutGuard implements CanActivate {
  private router = inject(Router);
  private store = inject(Store);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // We should only allow navigation for the sign-nda page since it's the only page that the user
    // should go inside with a full access token
    return this.store.select(TokenSelectors.selectTokenStateValue).pipe(
      switchMap(token => {
        if (!token) return of(true);
        if (!state.url.endsWith('sign-nda')) return of(this.router.createUrlTree(['/']));
        // We need to check if the user signed the NDA before allowing navigation to that screen
        return this.store.select(AuthSelectors.selectIamAccount).pipe(
          skipWhile(iamAccount => !iamAccount),
          take(1),
          map(iamAccount => {
            if (iamAccount.needsNda && !iamAccount.ndaSigned) {
              return true;
            }
            return this.router.createUrlTree(['/']);
          })
        );
      })
    );
  }
}
